import { makeAutoObservable, observable, runInAction } from "mobx";
import { track } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import {
  IntakeSubmissionActivityService,
  IntakeSubmissionCommentService,
} from "@/services/intake.service";
import type { TIntakeSubmissionActivity, TIntakeSubmissionComment } from "@/types/intake";

/** Submission activity feed, keyed by submission id. Mirrors
 *  ``ProjectActivityStore``. */
export class SubmissionActivityStore {
  activitiesBySubmission = observable.map<string, TIntakeSubmissionActivity[]>();
  loading = false;
  private service: IntakeSubmissionActivityService;

  constructor(_rootStore: RootStore, service?: IntakeSubmissionActivityService) {
    this.service = service ?? new IntakeSubmissionActivityService();
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get(submissionId: string): TIntakeSubmissionActivity[] {
    return this.activitiesBySubmission.get(submissionId) ?? [];
  }

  /** Revalidate the feed after a mutation, but only when it has been fetched
   *  before — an unopened feed fetches fresh on first open anyway. */
  async refreshIfCached(workspaceSlug: string, submissionId: string): Promise<void> {
    if (!this.activitiesBySubmission.has(submissionId)) return;
    try {
      await this.fetch(workspaceSlug, submissionId);
    } catch (error) {
      console.warn(
        `[SubmissionActivityStore] post-mutation activity refresh failed for submission ${submissionId}`,
        error,
      );
    }
  }

  async fetch(
    workspaceSlug: string,
    submissionId: string,
    limit = 50,
  ): Promise<TIntakeSubmissionActivity[]> {
    this.loading = true;
    try {
      const rows = await this.service.list(workspaceSlug, submissionId, limit);
      runInAction(() => {
        this.activitiesBySubmission.set(submissionId, rows);
      });
      return rows;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }
}

/** Submission comment feed, keyed by submission id. Mirrors
 *  ``ProjectCommentsStore``. */
export class SubmissionCommentsStore {
  commentsBySubmission = observable.map<string, TIntakeSubmissionComment[]>();
  loading = false;
  private service: IntakeSubmissionCommentService;
  private rootStore: RootStore;

  constructor(rootStore: RootStore, service?: IntakeSubmissionCommentService) {
    this.rootStore = rootStore;
    this.service = service ?? new IntakeSubmissionCommentService();
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get(submissionId: string): TIntakeSubmissionComment[] {
    return this.commentsBySubmission.get(submissionId) ?? [];
  }

  async fetch(workspaceSlug: string, submissionId: string): Promise<TIntakeSubmissionComment[]> {
    this.loading = true;
    try {
      const rows = await this.service.list(workspaceSlug, submissionId);
      runInAction(() => {
        this.commentsBySubmission.set(submissionId, rows);
      });
      return rows;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async create(
    workspaceSlug: string,
    submissionId: string,
    data: Partial<TIntakeSubmissionComment>,
  ): Promise<TIntakeSubmissionComment> {
    const created = await this.service.create(workspaceSlug, submissionId, data);
    runInAction(() => {
      const existing = this.commentsBySubmission.get(submissionId) ?? [];
      this.commentsBySubmission.set(submissionId, [created, ...existing]);
    });
    // The backend logs a "commented" activity row; revalidate an already-open
    // activity feed so the comment shows up there without a manual refresh.
    void this.rootStore.submissionActivity.refreshIfCached(workspaceSlug, submissionId);
    // The comments drawer on the request's page is the only surface that
    // writes comments, hence the fixed source.
    track("intake_submission_comment_posted", { request_id: submissionId, source: "side_panel" });
    return created;
  }

  async remove(workspaceSlug: string, submissionId: string, commentId: string): Promise<void> {
    await this.service.remove(workspaceSlug, submissionId, commentId);
    track("intake_submission_comment_deleted", {
      request_id: submissionId,
      comment_id: commentId,
      source: "side_panel",
    });
    runInAction(() => {
      const existing = this.commentsBySubmission.get(submissionId) ?? [];
      this.commentsBySubmission.set(
        submissionId,
        existing.filter((c) => c.id !== commentId),
      );
    });
    void this.rootStore.submissionActivity.refreshIfCached(workspaceSlug, submissionId);
  }
}
